import { useEffect, useMemo, useRef, useState } from "react";
import { OrderApi, useGetOrdersQuery } from "@/redux/services/order/order.api";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import { useMercureSubscription } from "@/lib/hooks";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import {
  cn,
  getOrderStatusText,
  getOrderTypeLabel,
} from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useElapsedTime } from "@/hooks/useElapsedTime";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import { normalizeOrder } from "@/redux/services/order/normalizeOrder";
import { endOfDay, startOfDay } from "date-fns";

const CASHIER_STATUSES: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
  "picked_up",
  "delivered",
];

const WAITER_STATUSES: OrderStatus[] = ["pending", "ready"];

export function MiniOrderCard({
  order,
  onClick,
}: {
  order: Order;
  onClick: () => void;
}) {
  const {
    text: elapsed,
    colorClass: elapsedColor,
    isTerminal,
  } = useElapsedTime(order.createdAt, order.status);

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 flex items-center gap-2.5 bg-card rounded-xl border border-border px-2.5 py-2 text-left hover:shadow-md transition-shadow active:bg-muted"
    >
      {order.orderType === "dine_in" && (
        <span className="bg-primary-light text-primary font-bold text-base rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0">
          {order.restaurantTable
            ? order.restaurantTable.code ?? "?"
            : "?"}
        </span>
      )}
      <span className="text-xl font-extrabold tabular-nums whitespace-nowrap leading-none">
        #{order.dailySequence ?? order.orderNumber}
      </span>
      <Badge
        status={order.status}
        className="text-xs px-2 py-0.5 whitespace-nowrap"
      >
        {getOrderStatusText(order.status)}
      </Badge>
      {order.orderType && order.orderType !== "dine_in" && (
        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
          {getOrderTypeLabel(order.orderType)}
        </span>
      )}
      <span
        className={cn(
          "text-xs font-semibold whitespace-nowrap",
          isTerminal ? "text-muted-foreground" : elapsedColor
        )}
      >
        {elapsed}
      </span>
    </button>
  );
}

const PosActiveOrders: React.FC = () => {
  const dispatch = useAppDispatch();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const restaurantId = restaurant?.id ?? "";

  const isWaiter = currentUser?.roles?.includes(Roles.waiter) ?? false;
  const statuses = isWaiter ? WAITER_STATUSES : CASHIER_STATUSES;

  const todayRange = useMemo(() => {
    const now = new Date();
    return {
      after: startOfDay(now).toISOString(),
      before: endOfDay(now).toISOString(),
    };
  }, []);

  const queryArgs = useMemo(() => {
    const params: Record<string, any> = {
      status: statuses.join(","),
      limit: 30,
      "order[createdAt]": "asc" as const,
      "createdAt[after]": todayRange.after,
      "createdAt[before]": todayRange.before,
    };
    // Cashier only cares about unpaid orders to follow up on payment.
    if (!isWaiter) params.isPaid = false;
    return { restaurantId, query: params };
  }, [restaurantId, statuses, isWaiter, todayRange]);

  const { data: ordersData } = useGetOrdersQuery(queryArgs, {
    skip: !restaurantId,
  });

  const orders = useMemo(() => ordersData?.results ?? [], [ordersData]);

  const [selectedOrder, setSelectedOrder] = useState<{
    id: string;
    orderNumber: string;
  } | null>(null);

  // Mercure events patch the cached list directly — fast, no refetch round trip.
  const orderEvent = useMercureSubscription<Order>(
    restaurantId ? `/api/restaurants/${restaurantId}/orders` : null
  );

  useEffect(() => {
    if (!orderEvent) return;
    const newOrder = normalizeOrder(orderEvent.data);
    if (!newOrder?.id) return;

    const matchesStatus = statuses.includes(newOrder.status);
    const matchesPaidFilter = isWaiter || !newOrder.isPaid;
    const shouldShow = matchesStatus && matchesPaidFilter;

    dispatch(
      OrderApi.util.updateQueryData("getOrders", queryArgs, (draft) => {
        const idx = draft.results.findIndex((o) => o.id === newOrder.id);
        if (shouldShow) {
          if (idx !== -1) {
            draft.results[idx] = newOrder;
          } else {
            draft.results.push(newOrder);
            draft.count = (draft.count ?? 0) + 1;
          }
        } else if (idx !== -1) {
          draft.results.splice(idx, 1);
          draft.count = Math.max(0, (draft.count ?? 0) - 1);
        }
      })
    );
  }, [orderEvent, statuses, isWaiter, dispatch, queryArgs]);

  // Horizontal scroll affordances — fade overlays hint that more content
  // exists off-screen on either side.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => {
      setShowLeftFade(el.scrollLeft > 8);
      setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [orders.length]);

  const sectionLabel = isWaiter
    ? "En attente / Prêtes"
    : "Commandes non payées";

  return (
    <>
      {orders.length > 0 && (
        <div>
          <div className="flex items-center gap-2.5 mb-3">
            <h2 className="text-base font-semibold text-foreground">
              {sectionLabel}
            </h2>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">
              {orders.length}
            </span>
          </div>
          <div className="relative">
            <div
              ref={scrollerRef}
              className="flex gap-3 overflow-x-auto pb-2 scroll-hidden"
            >
              {orders.map((order) => (
                <MiniOrderCard
                  key={order.id}
                  order={order}
                  onClick={() =>
                    setSelectedOrder({
                      id: order.id,
                      orderNumber: order.orderNumber,
                    })
                  }
                />
              ))}
            </div>
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute left-0 top-0 bottom-2 w-12 bg-gradient-to-r from-background to-transparent transition-opacity",
                showLeftFade ? "opacity-100" : "opacity-0"
              )}
            />
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-background to-transparent transition-opacity",
                showRightFade ? "opacity-100" : "opacity-0"
              )}
            />
          </div>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailsDrawer
          orderId={selectedOrder.id}
          orderNumber={selectedOrder.orderNumber}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </>
  );
};

export default PosActiveOrders;
